/**
 * Filtro por conductor en blacklist (BB-2608), sobre `criterios.isDriverBlacklisted` del contrato
 * (BB-2607).
 *
 * Tres posiciones y no un booleano, porque el contrato tiene tres estados: ausente = todos (el SQL de
 * siempre), `false` = los excluye, `true` = solo esos. "Todos" NO manda la clave, a propósito: así un
 * preview sin tocar el filtro es byte a byte el de antes, y los viajes de conductores en blacklist se
 * siguen pagando si Ops confirma sin cambiarlo — igual que hasta ahora. Decidido en el ticket.
 *
 * Es el flag ACTUAL de dbt, no el del día del viaje. Pagar o no a estos conductores lo decide Ops con
 * este filtro: Backend no los frena y los paga si se mandan (comprobado el 2026-10-26). Solo el
 * 2026-09-30 y el 2026-10-01 Backend revirtió a propósito su certificación, mientras este filtro no
 * existía ("Certification rolled back: driver or passenger is blacklisted").
 */
import type { Criterios, Resumen } from '@caes-payments/shared';

export type FiltroBlacklist = 'todos' | 'excluir' | 'solo';

export const FILTRO_BLACKLIST_VACIO: FiltroBlacklist = 'todos';

/** Texto de cada posición del control. Los tres tienen que ser inequívocos leídos sueltos. */
export const ETIQUETA_FILTRO_BLACKLIST: Record<FiltroBlacklist, string> = {
  todos: 'Todos',
  excluir: 'Sin blacklist',
  solo: 'Solo blacklist',
};

/** Escribe el criterio. "Todos" lo QUITA, nunca manda `undefined` ni un valor por defecto. */
export function aplicarBlacklist(criterios: Criterios, filtro: FiltroBlacklist): Criterios {
  const { isDriverBlacklisted: _anterior, ...resto } = criterios;
  if (filtro === 'todos') return resto;
  return { ...resto, isDriverBlacklisted: filtro === 'solo' };
}

/** Cómo se nombra cada bucket de `porIsDriverBlacklisted` en el resumen. */
export function etiquetaBucketBlacklist(isDriverBlacklisted: boolean): string {
  return isDriverBlacklisted ? 'Conductor en blacklist' : 'Conductor fuera de blacklist';
}

/**
 * Texto del criterio para el alcance y el registro, o null si no acotó nada. Null y no "todos": una
 * confirmación anterior a este filtro no tenía esa dimensión, y decir "todos" afirmaría un filtro que
 * entonces no existía.
 */
export function describirCriterioBlacklist(isDriverBlacklisted: boolean | undefined): string | null {
  if (isDriverBlacklisted === undefined) return null;
  return isDriverBlacklisted
    ? 'solo conductores en blacklist'
    : 'sin conductores en blacklist';
}

/**
 * Si el resumen DEMUESTRA que el backend no aplicó el filtro de blacklist que se le pidió.
 *
 * El backend ignora en silencio un criterio cuya columna no existe en la tabla del programa (guard
 * `hasColumn` de `queryBuilder.ts`) y entonces devuelve `porIsDriverBlacklisted: []`. Hoy las dos
 * tablas tienen la columna, así que no pasa; pero si un programa nuevo no la tuviera, la pantalla
 * diría "sin conductores en blacklist" y el conjunto —lo que se pagaría— los incluiría. El desglose
 * es la prueba: con el filtro aplicado, todos sus buckets tienen el valor pedido.
 *
 * Solo mira lo que llegó (los criterios que el backend devolvió y su resumen); nunca recalcula nada.
 */
export function blacklistNoAplicado(
  criterios: Criterios,
  resumen: Pick<Resumen, 'nViajes' | 'porIsDriverBlacklisted'>,
): boolean {
  const pedido = criterios.isDriverBlacklisted;
  if (pedido === undefined || resumen.nViajes === 0) return false;
  const buckets = resumen.porIsDriverBlacklisted ?? [];
  return buckets.length === 0 || buckets.some((b) => b.isDriverBlacklisted !== pedido);
}
